import Head from 'next/head';
import type { GetStaticPaths, GetStaticProps } from 'next';
import { PortableText } from '@portabletext/react';

import {
  buildContentPagePath,
  buildSanityImageSrcSet,
  buildSanityImageUrl,
  getAllContentPageSlugsQuery,
  getContentPage,
  isReservedContentPageSlug,
  sanityClient
} from '@lib/sanity';
import type { SanityContentPage } from '@lib/sanity';
import { FFH_HOSTNAME, SITE_NAME } from '@lib/constants';
import { scaleShareImageDimensions } from '@lib/article-seo';
import { buildArticlePortableTextComponents } from '@components/article/portable-text-components';
import { ARTICLE_TOKENS } from '@components/article/design-tokens';
import { extractHeadings } from '@lib/article-blocks/headings';
import { hydrateArticleBlocks } from '@lib/article-blocks/hydrate';
import type { ArticleBlockData } from '@lib/article-blocks/types';
import WideLayout from '@components/layout/wide';
import { MissingPropsRecovery } from '@components/missing-props-recovery';

// Same ISR windows as /articles: time-based until the publish webhook
// (FFH-2627) lands, and an expiring 404 so a publish race cannot cache a miss
// until the next deploy.
export const REVALIDATE_SECONDS = 600;
export const NOT_FOUND_REVALIDATE_SECONDS = 60;
const OG_IMAGE_WIDTH = 1200;
const HERO_IMAGE_WIDTHS = [640, 960, 1280, 1600];
const HERO_IMAGE_SIZES = '(min-width: 48rem) 44rem, 100vw';
const HERO_IMAGE_DEFAULT_WIDTH = 1280;

const PROPS_RELOAD_MARKER_KEY = 'ffh-content-page-props-reload';
const MISSING_PROPS_RUM_CONTEXT = 'content-page-missing-props';
const CONTENT_PAGES_FALLBACK_PATH = '/';

export type ContentPageProps = {
  /**
   * Always supplied by getStaticProps, but a client-side navigation can
   * render the page without it when the _next/data fetch fails, so the
   * component must tolerate its absence. See MissingPropsRecovery.
   */
  page?: SanityContentPage;
  blockData: ArticleBlockData;
};

export const buildContentPageMetaTitle = (page: SanityContentPage): string =>
  page.seo?.metaTitle || `${page.title} | ${SITE_NAME}`;

export const ContentPage = ({ page, blockData }: ContentPageProps) => {
  if (!page) {
    return (
      <MissingPropsRecovery
        context={MISSING_PROPS_RUM_CONTEXT}
        reloadMarkerKey={PROPS_RELOAD_MARKER_KEY}
        fallbackPath={CONTENT_PAGES_FALLBACK_PATH}
        errorMessage="Content page props missing"
      />
    );
  }

  const metaTitle = buildContentPageMetaTitle(page);
  const metaDescription = page.seo?.metaDescription;
  const canonicalUrl =
    page.seo?.canonicalUrl ||
    `${FFH_HOSTNAME}${buildContentPagePath(page.slug.current)}`;
  const shareImage = page.seo?.ogImage ?? page.heroImage;
  const ogImageUrl = shareImage?.asset
    ? buildSanityImageUrl(shareImage).width(OG_IMAGE_WIDTH).url()
    : undefined;
  const shareImageDimensions = scaleShareImageDimensions(
    OG_IMAGE_WIDTH,
    shareImage?.asset?.metadata?.dimensions
  );
  const { heroImage } = page;
  const { width: heroWidth, height: heroHeight } =
    heroImage?.asset?.metadata?.dimensions ?? {};
  // Ids are consumed as headings render, so the map gets its own copy.
  const headings = extractHeadings(page.body);
  const components = buildArticlePortableTextComponents(blockData, [
    ...headings
  ]);

  return (
    <>
      <Head>
        <title>{metaTitle}</title>
        {metaDescription && (
          <meta name="description" content={metaDescription} />
        )}
        <link rel="canonical" href={canonicalUrl} key="canonical" />
        <meta property="og:title" content={metaTitle} key="og:title" />
        <meta property="og:url" content={canonicalUrl} key="og:url" />
        {metaDescription && (
          <meta
            property="og:description"
            content={metaDescription}
            key="og:description"
          />
        )}
        {ogImageUrl && (
          <meta property="og:image" content={ogImageUrl} key="og:image" />
        )}
        {shareImageDimensions && (
          <meta
            property="og:image:width"
            content={String(shareImageDimensions.width)}
            key="og:image:width"
          />
        )}
        {shareImageDimensions && (
          <meta
            property="og:image:height"
            content={String(shareImageDimensions.height)}
            key="og:image:height"
          />
        )}
        <meta property="og:type" content="website" key="og:type" />
        {page.seo?.isHiddenFromSearch && (
          <meta name="robots" content="noindex,nofollow" />
        )}
      </Head>
      <WideLayout>
        <div className="bg-white">
          <div className="mx-auto max-w-[1240px] px-4 pb-20 pt-10 sm:px-8 sm:pt-14">
            <article
              className="article mx-auto w-full max-w-[700px]"
              data-cy="content-page"
            >
              <header>
                {heroImage?.asset && (
                  <figure className="mb-8">
                    <div className="aspect-video overflow-hidden rounded-[14px] bg-[#eff2f4]">
                      <img
                        src={buildSanityImageUrl(heroImage)
                          .width(HERO_IMAGE_DEFAULT_WIDTH)
                          .url()}
                        srcSet={buildSanityImageSrcSet(
                          heroImage,
                          HERO_IMAGE_WIDTHS
                        )}
                        sizes={HERO_IMAGE_SIZES}
                        alt={heroImage.alt ?? ''}
                        width={heroWidth}
                        height={heroHeight}
                        className="h-full w-full object-cover"
                        fetchPriority="high"
                      />
                    </div>
                    {(heroImage.caption || heroImage.credit) && (
                      <figcaption
                        className={`mt-2.5 text-[13px] ${ARTICLE_TOKENS.mutedLight}`}
                      >
                        {heroImage.caption}
                        {heroImage.credit && (
                          <span className={ARTICLE_TOKENS.mutedLighter}>
                            {heroImage.caption ? ' · ' : ''}
                            {heroImage.credit}
                          </span>
                        )}
                      </figcaption>
                    )}
                  </figure>
                )}
                <h1
                  className={`mb-8 font-heading text-[34px] font-bold leading-[1.15] tracking-[-0.02em] sm:text-[42px] ${ARTICLE_TOKENS.ink} [text-wrap:balance]`}
                >
                  {page.title}
                </h1>
              </header>
              <div className="content-body">
                <PortableText value={page.body} components={components} />
              </div>
            </article>
          </div>
        </div>
      </WideLayout>
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = await sanityClient.fetch<{ slug: string }[]>(
    getAllContentPageSlugsQuery
  );

  return {
    paths: slugs
      .filter((entry) => entry.slug && !isReservedContentPageSlug(entry.slug))
      .map((entry) => ({ params: { slug: entry.slug } })),
    fallback: 'blocking'
  };
};

export const getStaticProps: GetStaticProps<ContentPageProps> = async ({
  params
}) => {
  const slug = params?.slug as string;
  // Unreachable behind a permanent redirect anyway; refusing it here stops
  // a build for a slug the site can never serve.
  if (isReservedContentPageSlug(slug)) {
    return { notFound: true, revalidate: NOT_FOUND_REVALIDATE_SECONDS };
  }

  const page = await getContentPage(slug);

  if (!page) {
    return { notFound: true, revalidate: NOT_FOUND_REVALIDATE_SECONDS };
  }

  const blockData = await hydrateArticleBlocks(page.body);

  return {
    props: { page, blockData },
    revalidate: REVALIDATE_SECONDS
  };
};

export default ContentPage;
